import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spacer,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Context } from "../../ContextWrapper";
import CustomTableContainer from "../../theme/components/tableContainer";
import { customCurrencyFormat } from "../../utils";

function formatDate(dateString) {
  const date = new Date(dateString);

  const options = { year: "numeric", month: "long" };
  return date.toLocaleDateString("en-US", options);
}

export const MonthlyIndicator = ({ customer_id, year_id, turnBack }) => {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [indicatorList, setIndicatorList] = useState([]);
  const [yearData, setYearData] = useState({});
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [focusedInputName, setFocusedInputName] = useState(null);
  const [resourceCategories, setResourceCategories] = useState([]);
  const context = React.useContext(Context);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch Monthly Indicators
        const indicatorsResponse = await axios.get(
          `/api/v2/customers/${customer_id}/yearlybudget/${year_id}/monthlyindicators/`,
          { withCredentials: true }
        );
        setIndicatorList(indicatorsResponse.data);

        // Fetch Year Data
        const yearResponse = await axios.get(
          `/api/v2/customers/${customer_id}/yearlybudget/${year_id}`,
          { withCredentials: true }
        );
        setYearData(yearResponse.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customer_id, year_id]);

  useEffect(() => {
    const fetchResourceCategoryData = async () => {
      setLoading(true);
      try {
        const indicatorsResponse = await axios.get(
          `/api/v2/resourcecategories/`,
          { withCredentials: true }
        );
        setResourceCategories(indicatorsResponse.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchResourceCategoryData();
  }, [customer_id]);

  const handleSubmit = async (event, updatedIndicator = null) => {
    event.preventDefault();
    setLoading(true);

    try {
      if (updatedIndicator) {
        const indicatorUpdateUrl = `/api/v2/customers/${customer_id}/yearlybudget/${yearData.id}/monthlyindicators/${updatedIndicator.id}/`;

        const indicatorResponse = await axios.patch(
          indicatorUpdateUrl,
          updatedIndicator
        );

        if (indicatorResponse.status < 300) {
          setIndicatorList((prevIndicators) =>
            prevIndicators.map((indicator) =>
              indicator.id === updatedIndicator.id
                ? indicatorResponse.data
                : indicator
            )
          );
          context.fireToast("Success", "Indicator has been updated", "success");
        } else {
          context.fireToast(
            "Error",
            indicatorResponse.request.responseText,
            "error"
          );
        }
      } else {
        const yearUpdateUrl = `/api/v2/customers/${customer_id}/yearlybudget/${yearData.id}/`;

        delete yearData.date;

        const yearResponse = await axios.patch(yearUpdateUrl, yearData);
        if (yearResponse.status < 300) {
          setYearData(yearResponse.data);
        } else {
          context.fireToast(
            "Error",
            yearResponse.request.responseText,
            "error"
          );
        }

        const updatedIndicators = await Promise.all(
          indicatorList.map(async (indicator) => {
            if (!indicator.id) return indicator; // Skip if ID is missing

            const indicatorUpdateUrl = `/api/v2/customers/${customer_id}/yearlybudget/${yearData.id}/monthlyindicators/${indicator.id}/`;
            const indicatorResponse = await axios.patch(
              indicatorUpdateUrl,
              indicator
            );

            if (indicatorResponse.status < 300) {
              return indicatorResponse.data; // Return the updated indicator
            } else {
              context.fireToast(
                "Error",
                indicatorResponse.request.responseText,
                "error"
              );
              return indicator; // Return the old indicator if there's an error
            }
          })
        );

        // Set the updated indicators state
        setIndicatorList(updatedIndicators);
        context.fireToast("Success", "Year data updated", "success");
      }
    } catch (err) {
      console.error("There was an error updating:", err);
      context.fireToast(
        "Error",
        "An error occurred during the update",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleIndicatorPLInputChange = (e, indicatorId) => {
    const { name, value } = e.target;

    setIndicatorList((prevList) => {
      return prevList.map((indicator) => {
        if (indicator.id !== indicatorId) return indicator;

        // Preserve raw input for the current field
        const updatedIndicator = { ...indicator, [name]: value };

        // Recalculate profit using the updated values
        const profit =
          100 -
          ((parseFloat(updatedIndicator.cogs_pl) || 0) +
            (parseFloat(updatedIndicator.marketing_pl) || 0) +
            (parseFloat(updatedIndicator.team_pl) || 0) +
            (parseFloat(updatedIndicator.practice_cost_pl) || 0) +
            (parseFloat(updatedIndicator.overhead_pl) || 0) +
            (parseFloat(updatedIndicator.business_development_pl) || 0) +
            (parseFloat(updatedIndicator.taxes_pl) || 0));

        return {
          ...updatedIndicator,
          profit_pl: String(profit !== 100 ? profit : 0),
        };
      });
    });
  };

  const handleInputChange = (e, indicatorId) => {
    const { name, value } = e.target;

    setIndicatorList((prevList) =>
      prevList.map((indicator) =>
        indicator.id === indicatorId
          ? { ...indicator, [name]: value }
          : indicator
      )
    );
  };

  const handlePLInputChange = (e) => {
    const { name, value } = e.target;

    setYearData((prev) => {
      // Preserve raw input for the current field
      const updatedData = { ...prev, [name]: value };

      // Recalculate profit using valid numbers only
      const profit =
        100 -
        ((parseFloat(updatedData.cogs_pl) || 0) +
          (parseFloat(updatedData.marketing_pl) || 0) +
          (parseFloat(updatedData.team_pl) || 0) +
          (parseFloat(updatedData.practice_cost_pl) || 0) +
          (parseFloat(updatedData.overhead_pl) || 0) +
          (parseFloat(updatedData.business_development_pl) || 0) +
          (parseFloat(updatedData.taxes_pl) || 0));

      return { ...updatedData, profit_pl: String(profit) };
    });
  };

  const handleNormalInputChange = (e) => {
    const { name, value } = e.target;

    setYearData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggle = () => {
    setSelectedIndicator({});
    setIsModalOpen(!isModalOpen);
  };

  const calcIncomeOffPercentage = () => {
    const percentage_increase =
      parseFloat(yearData.income_percentage_inc) / 100;
    let income_inc = parseFloat(yearData.income_starting_point);

    const updatedIndicators = indicatorList.map((indicator) => {
      income_inc += income_inc * percentage_increase;
      return { ...indicator, income_forecast: income_inc.toFixed(2) };
    });

    setIndicatorList(updatedIndicators);
    context.fireToast("Success", "Income Growth Applied", "success");
  };

  return (
    <Stack textAlign={"center"} overflowX={"auto"} maxW={"100%"}>
      {loading && (
        <FontAwesomeIcon
          className="float-end fa-spin"
          icon={faSpinner}
          color="green"
        />
      )}
      <Stack align="center">
        <CustomTableContainer>
          <IndicatorModal
            isOpen={isModalOpen}
            toggle={toggle}
            selectedIndicator={selectedIndicator}
            setSelectedIndicator={setSelectedIndicator}
            title="Comment for month"
            indicatorList={indicatorList}
            resourceCategories={resourceCategories}
            loading={loading}
            handleSubmit={handleSubmit}
          />
          <form id="indicatorForm" onSubmit={(e) => handleSubmit(e)}>
            <Flex pb="5">
              <Box>
                <Heading as="h3" size="lg">
                  Customer Monthly Targets
                </Heading>
              </Box>
              <Spacer />
              <Stack direction="row" spacing={6} align="center">
                <Input
                  width={"75px"}
                  name="income_percentage_inc"
                  value={
                    focusedInputName === "income_percentage_inc"
                      ? yearData.income_percentage_inc ?? 0
                      : customCurrencyFormat(
                          yearData.income_percentage_inc ?? 0,
                          true,
                          false,
                          true
                        )
                  }
                  onFocus={() => setFocusedInputName("income_percentage_inc")}
                  onBlur={() => setFocusedInputName(null)}
                  onChange={(e) => handleNormalInputChange(e)}
                />
                <Button
                  onClick={() => {
                    calcIncomeOffPercentage();
                  }}
                >
                  Apply income growth
                </Button>
              </Stack>
              <Spacer />
              <Box>
                <Stack direction="row" spacing={6} align="center">
                  <Button
                    onClick={() => {
                      setIsModalOpen(true);
                    }}
                  >
                    Create Comment
                  </Button>
                  <Button type="submit" form="indicatorForm">
                    Save
                  </Button>
                </Stack>
              </Box>
            </Flex>

            <Box
              maxHeight="calc(100vh - 350px)"
              maxW={"calc(100vw - 500px)"}
              bg="white"
              overflowY={"auto"}
              overflowX={"auto"}
            >
              <Table variant="unstyled" width={"100%"}>
                <Thead position="sticky" top={-1} zIndex={1}>
                  <Tr>
                    <Th textAlign={"center"}>Accounts</Th>
                    <Th textAlign={"center"}>Income Start Point / P&L %</Th>
                    {indicatorList ? (
                      indicatorList.map((indicator) => (
                        <Th
                          textAlign={"center"}
                          key={formatDate(indicator.date)}
                        >
                          {" "}
                          {formatDate(indicator.date)}
                        </Th>
                      ))
                    ) : (
                      <></>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td textAlign={"left"} key={"labels"}>
                      <VStack align={"start"}>
                        <Box p={"9px 0px"}>Income</Box>
                        <Box p={"9px 0px"}>COGS</Box>
                        <Box p={"9px 0px"}>Marketing</Box>
                        <Box p={"9px 0px"}>Team</Box>
                        <Box p={"9px 0px"}>Practice Cost</Box>
                        <Box p={"9px 0px"}>Overhead</Box>
                        <Box p={"9px 0px"}>Business Development</Box>
                        <Box p={"9px 0px"}>Taxes</Box>
                        <Box p={"9px 0px"}>Net Profit</Box>
                      </VStack>
                    </Td>
                    <Td key={yearData.id}>
                      <VStack>
                        <Box p={0}>
                          <Input
                            width={"150px"}
                            textAlign={"center"}
                            name="income_starting_point"
                            value={
                              focusedInputName === "income_starting_point"
                                ? yearData.income_starting_point ?? 0
                                : customCurrencyFormat(
                                    yearData.income_starting_point ?? 0,
                                    false,
                                    false,
                                    true,
                                    true
                                  )
                            }
                            onFocus={() =>
                              setFocusedInputName("income_starting_point")
                            }
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handleNormalInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="cogs_pl"
                            value={
                              focusedInputName === "cogs_pl"
                                ? yearData.cogs_pl
                                : customCurrencyFormat(
                                    yearData.cogs_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("cogs_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="marketing_pl"
                            value={
                              focusedInputName === "marketing_pl"
                                ? yearData.marketing_pl
                                : customCurrencyFormat(
                                    yearData.marketing_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("marketing_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="team_pl"
                            value={
                              focusedInputName === "team_pl"
                                ? yearData.team_pl
                                : customCurrencyFormat(
                                    yearData.team_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("team_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="practice_cost_pl"
                            value={
                              focusedInputName === "practice_cost_pl"
                                ? yearData.practice_cost_pl
                                : customCurrencyFormat(
                                    yearData.practice_cost_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() =>
                              setFocusedInputName("practice_cost_pl")
                            }
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="overhead_pl"
                            value={
                              focusedInputName === "overhead_pl"
                                ? yearData.overhead_pl
                                : customCurrencyFormat(
                                    yearData.overhead_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("overhead_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="business_development_pl"
                            value={
                              focusedInputName === "business_development_pl"
                                ? yearData.business_development_pl
                                : customCurrencyFormat(
                                    yearData.business_development_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() =>
                              setFocusedInputName("business_development_pl")
                            }
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="taxes_pl"
                            value={
                              focusedInputName === "taxes_pl"
                                ? yearData.taxes_pl
                                : customCurrencyFormat(
                                    yearData.taxes_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("taxes_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="profit_pl"
                            border={"none"}
                            value={
                              focusedInputName === "profit_pl"
                                ? yearData.profit_pl
                                : customCurrencyFormat(
                                    yearData.profit_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            isReadOnly
                          />
                        </Box>
                      </VStack>
                    </Td>

                    {indicatorList ? (
                      indicatorList.map((indicator) => (
                        <IndicatorRow
                          key={indicator.id}
                          indicator={indicator}
                          customer_id={customer_id}
                          handleInputChange={handleInputChange}
                          handlePLInputChange={handleIndicatorPLInputChange}
                        />
                      ))
                    ) : (
                      <></>
                    )}
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          </form>
          <Stack align={"start"}>
            <Button
              width={"300px"}
              m={"10px 0px 0px 0px"}
              onClick={() => turnBack(false)}
            >
              Back to year select
            </Button>
          </Stack>
        </CustomTableContainer>
      </Stack>
    </Stack>
  );
};

const IndicatorRow = ({
  indicator,
  handleInputChange,
  handlePLInputChange,
}) => {
  const [focusedInputName, setFocusedInputName] = useState(null);

  return (
    <Td key={indicator.id}>
      <VStack alignItems={"start"}>
        <Box>
          <Input
            width={"150px"}
            textAlign={"center"}
            name="income_forecast"
            value={
              focusedInputName === "income_forecast"
                ? indicator.income_forecast
                : customCurrencyFormat(
                    indicator.income_forecast,
                    false,
                    false,
                    true,
                    true
                  )
            }
            onFocus={() => setFocusedInputName("income_forecast")}
            onBlur={() => setFocusedInputName(null)}
            onChange={(e) => handleInputChange(e, indicator.id)}
          />
        </Box>
        <Box p={"0px 0px"} height={"40px"}>
          <HStack>
            <Input
              width={"75px"}
              name="cogs_pl"
              value={
                focusedInputName === "cogs_pl"
                  ? indicator.cogs_pl
                  : customCurrencyFormat(indicator.cogs_pl, true, false, true)
              }
              onFocus={() => setFocusedInputName("cogs_pl")}
              onBlur={() => setFocusedInputName(null)}
              onChange={(e) => handlePLInputChange(e, indicator.id)}
            />
            <Text>
              {customCurrencyFormat(
                indicator.cogs_forecast,
                false,
                false,
                true,
                true
              )}
            </Text>
          </HStack>
        </Box>
        <Box p={"0px 0px"} height={"40px"}>
          <HStack>
            <Input
              width={"75px"}
              name="marketing_pl"
              value={
                focusedInputName === "marketing_pl"
                  ? indicator.marketing_pl
                  : customCurrencyFormat(
                      indicator.marketing_pl,
                      true,
                      false,
                      true
                    )
              }
              onFocus={() => setFocusedInputName("marketing_pl")}
              onBlur={() => setFocusedInputName(null)}
              onChange={(e) => handlePLInputChange(e, indicator.id)}
            />
            <Text>
              {customCurrencyFormat(
                indicator.marketing_forecast,
                false,
                false,
                true,
                true
              )}
            </Text>
          </HStack>
        </Box>
        <Box p={"0px 0px"} height={"40px"}>
          <HStack>
            <Input
              width={"75px"}
              name="team_pl"
              value={
                focusedInputName === "team_pl"
                  ? indicator.team_pl
                  : customCurrencyFormat(indicator.team_pl, true, false, true)
              }
              onFocus={() => setFocusedInputName("team_pl")}
              onBlur={() => setFocusedInputName(null)}
              onChange={(e) => handlePLInputChange(e, indicator.id)}
            />
            <Text>
              {customCurrencyFormat(
                indicator.team_forecast,
                false,
                false,
                true,
                true
              )}
            </Text>
          </HStack>
        </Box>
        <Box p={"0px 0px"} height={"40px"}>
          <HStack>
            <Input
              width={"75px"}
              name="practice_cost_pl"
              value={
                focusedInputName === "practice_cost_pl"
                  ? indicator.practice_cost_pl
                  : customCurrencyFormat(
                      indicator.practice_cost_pl,
                      true,
                      false,
                      true
                    )
              }
              onFocus={() => setFocusedInputName("practice_cost_pl")}
              onBlur={() => setFocusedInputName(null)}
              onChange={(e) => handlePLInputChange(e, indicator.id)}
            />
            <Text>
              {customCurrencyFormat(
                indicator.practice_cost_forecast,
                false,
                false,
                true,
                true
              )}
            </Text>
          </HStack>
        </Box>
        <Box p={"0px 0px"} height={"40px"}>
          <HStack>
            <Input
              width={"75px"}
              name="overhead_pl"
              value={
                focusedInputName === "overhead_pl"
                  ? indicator.overhead_pl
                  : customCurrencyFormat(
                      indicator.overhead_pl,
                      true,
                      false,
                      true
                    )
              }
              onFocus={() => setFocusedInputName("overhead_pl")}
              onBlur={() => setFocusedInputName(null)}
              onChange={(e) => handlePLInputChange(e, indicator.id)}
            />
            <Text>
              {customCurrencyFormat(
                indicator.overhead_forecast,
                false,
                false,
                true,
                true
              )}
            </Text>
          </HStack>
        </Box>
        <Box p={"0px 0px"} height={"40px"}>
          <HStack>
            <Input
              width={"75px"}
              name="business_development_pl"
              value={
                focusedInputName === "business_development_pl"
                  ? indicator.business_development_pl
                  : customCurrencyFormat(
                      indicator.business_development_pl,
                      true,
                      false,
                      true
                    )
              }
              onFocus={() => setFocusedInputName("business_development_pl")}
              onBlur={() => setFocusedInputName(null)}
              onChange={(e) => handlePLInputChange(e, indicator.id)}
            />
            <Text>
              {customCurrencyFormat(
                indicator.business_development_forecast,
                false,
                false,
                true,
                true
              )}
            </Text>
          </HStack>
        </Box>
        <Box p={"0px 0px"} height={"40px"}>
          <HStack>
            <Input
              width={"75px"}
              name="taxes_pl"
              value={
                focusedInputName === "taxes_pl"
                  ? indicator.taxes_pl
                  : customCurrencyFormat(indicator.taxes_pl, true, false, true)
              }
              onFocus={() => setFocusedInputName("taxes_pl")}
              onBlur={() => setFocusedInputName(null)}
              onChange={(e) => handlePLInputChange(e, indicator.id)}
            />
            <Text>
              {customCurrencyFormat(
                indicator.taxes_forecast,
                false,
                false,
                true,
                true
              )}
            </Text>
          </HStack>
        </Box>
        <Box p={"0px 0px"} height={"40px"}>
          <HStack>
            <Input
              width={"75px"}
              name="profit_pl"
              border={"none"}
              value={
                focusedInputName === "profit_pl"
                  ? indicator.profit_pl
                  : customCurrencyFormat(indicator.profit_pl, true, false, true)
              }
              readOnly
            />
            <Text>
              {customCurrencyFormat(
                indicator.net_operating_profit_forecast,
                false,
                false,
                true,
                true
              )}
            </Text>
          </HStack>
        </Box>
      </VStack>
    </Td>
  );
};

const IndicatorModal = ({
  isOpen,
  toggle,
  selectedIndicator,
  setSelectedIndicator,
  title,
  indicatorList,
  resourceCategories,
  loading,
  handleSubmit,
}) => {
  const confirmText = "Update";
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedResources, setSelectedResources] = useState(["", "", ""]);

  useEffect(() => {
    if (selectedIndicator && selectedIndicator.recommended_resources) {
      setSelectedResources(selectedIndicator.recommended_resources);
    }
  }, [selectedIndicator]);

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);

    const foundIndicator = indicatorList.find(
      (indicator) =>
        new Date(indicator.date).toISOString().split("T")[0] === date
    );

    if (foundIndicator) {
      setSelectedIndicator(foundIndicator);
    } else {
      setSelectedIndicator(null);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedIndicator({ ...selectedIndicator, [name]: value });
  };

  const handleResourceChange = (event, index) => {
    const newResourceId = event.target.value;

    const newSelectedResources = [...selectedResources];

    if (newResourceId === "") {
      newSelectedResources[index] = "";
    } else {
      newSelectedResources[index] = Number(newResourceId);
    }

    setSelectedResources(newSelectedResources);

    const updatedRecommendedResources = newSelectedResources.filter(
      (id) => id !== ""
    );
    const updatedIndicator = {
      ...selectedIndicator,
      recommended_resources: updatedRecommendedResources,
    };

    setSelectedIndicator(updatedIndicator);
  };

  const getFilteredResources = (index) => {
    return resourceCategories.filter((resource) => {
      return (
        !selectedResources.includes(resource.id) ||
        selectedResources[index] === resource.id
      );
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setSelectedDate("");
        toggle();
        setSelectedIndicator(null);
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody maxHeight={"500px"} overflowY="auto">
          <form
            id="IndicatorForm"
            onSubmit={(e) => handleSubmit(e, selectedIndicator)}
          >
            <FormControl>
              <FormLabel>Select Date</FormLabel>
              <Select
                placeholder="Select date"
                value={selectedDate}
                onChange={handleDateChange}
              >
                {indicatorList.map((indicator) => (
                  <option
                    key={indicator.id}
                    value={new Date(indicator.date).toISOString().split("T")[0]}
                  >
                    {formatDate(indicator.date)}
                  </option>
                ))}
              </Select>
            </FormControl>

            {selectedIndicator && (
              <Stack>
                <Text
                  fontSize="20px"
                  textDecoration={"underline"}
                  padding={"10px 0px"}
                >
                  Resource Recommendation
                </Text>

                {[0, 1, 2].map((index) => {
                  const recommendedResourceId = selectedResources[index];

                  return (
                    <FormControl key={index} mb={2}>
                      <FormLabel>Resource Recommendation {index + 1}</FormLabel>
                      <Select
                        placeholder={`Select resource ${index + 1}`}
                        value={recommendedResourceId || ""}
                        onChange={(event) => handleResourceChange(event, index)}
                      >
                        {getFilteredResources(index).map((resource) => (
                          <option key={resource.id} value={resource.id}>
                            {resource.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  );
                })}
                <Text
                  fontSize="20px"
                  textDecoration={"underline"}
                  padding={"10px 0px"}
                >
                  Performance Overview
                </Text>

                <FormControl>
                  <Textarea
                    h={"150px"}
                    type="text"
                    name="performance_overview"
                    placeholder="Enter performance review message here."
                    value={selectedIndicator.performance_overview || ""}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </Stack>
            )}
          </form>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => {
              setSelectedDate("");
              toggle();
              setSelectedIndicator(null);
            }}
            isDisabled={loading}
            colorScheme="blackAlpha"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="IndicatorForm"
            isDisabled={loading}
            ml={3}
          >
            {loading ? <Spinner size="sm" /> : confirmText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
